import React from "react";
import { Button } from "../../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";

function FilingDisclosure({ title, subtitle, defaultOpen = false, children }) {
  return (
    <details open={defaultOpen} className="rounded-lg border border-slate-200 bg-slate-50">
      <summary className="cursor-pointer list-none px-4 py-3">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-900">{title}</p>
            {subtitle ? <p className="mt-1 text-xs text-slate-500">{subtitle}</p> : null}
          </div>
          <span className="text-xs uppercase tracking-[0.16em] text-slate-400">Details anzeigen</span>
        </div>
      </summary>
      <div className="border-t border-slate-200 px-4 py-4">{children}</div>
    </details>
  );
}

function formatOwnerRoleLabel(value) {
  const labels = {
    employer: "Arbeitgeber",
    candidate: "Bewerber",
    authority: "Behoerde",
    third_party: "Dritter",
    internal: "Intern",
    both: "Gemeinsam",
    manual: "Manuell",
  };
  return labels[value] || value || "Ungeklaert";
}

function formatReviewBucketLabel(value) {
  const labels = {
    ready_to_sign: "Zur Unterschrift bereit",
    ready_to_upload: "Zur Einreichung bereit",
    prepared_but_incomplete: "Vorbereitet, aber unvollstaendig",
    manual_only: "Manuell erforderlich",
    missing_docs: "Fehlt noch",
  };
  return labels[value] || value || "In Arbeit";
}

function formatFillabilityClass(value) {
  const labels = {
    fillable_pdf: "Direkt vorbereitbar",
    structured_transfer_only: "Transferhilfe",
    manual_only: "Manuell erforderlich",
    reference: "Nur Referenz",
  };
  return labels[value] || value || "Unbekannt";
}

function formatRequestReadableState(requestEntry, formatRequestStatus) {
  if (!requestEntry) return "Offen";
  if (requestEntry.status === "fulfilled") return "Erledigt";
  if (requestEntry.status === "obsolete") return "Nicht mehr relevant";
  if (requestEntry.status === "expired") return "Abgelaufen";
  if (requestEntry.awaiting_employer_review || ["response_received", "response_under_review", "response_incomplete", "response_rejected"].includes(requestEntry.response_status)) {
    return "In Pruefung";
  }
  if (requestEntry.overdue) return "Ueberfaellig";
  if (requestEntry.due_soon) return "Bald faellig";
  if (requestEntry.status === "viewed") return "Vom Bewerber gesehen";
  if (requestEntry.status === "sent") return "Warten auf Rueckmeldung";
  if (requestEntry.status === "answered") return "Antwort eingegangen";
  return formatRequestStatus(requestEntry.status);
}

function formatRequestNextStep(requestEntry) {
  if (!requestEntry) return "Keine direkte Aktion offen.";
  if (requestEntry.status === "draft") return "Anfrage senden.";
  if (requestEntry.status === "fulfilled") return "Fuer dieses Thema ist aktuell nichts mehr offen.";
  if (requestEntry.status === "obsolete") return "Keine weitere Bearbeitung noetig.";
  if (requestEntry.awaiting_employer_review || ["response_received", "response_under_review"].includes(requestEntry.response_status)) {
    return "Rueckmeldung pruefen und freigeben.";
  }
  if (requestEntry.response_status === "response_incomplete") return "Nachforderung konkretisieren.";
  if (requestEntry.response_status === "response_rejected") return "Nacharbeit beim Bewerber anstossen.";
  if (requestEntry.overdue) return "Rueckmeldung des Bewerbers aktiv nachhalten.";
  return "Rueckmeldung des Bewerbers abwarten.";
}

function formatDocumentResponsibility(documentEntry, missingEntry) {
  if (missingEntry?.responsibility?.label) return missingEntry.responsibility.label;
  if (documentEntry?.candidate_document_dependency && documentEntry?.employer_document_dependency) return "Bewerber und Arbeitgeber";
  if (documentEntry?.candidate_document_dependency) return "Bewerber";
  if (documentEntry?.employer_document_dependency) return "Arbeitgeber";
  if (documentEntry?.signature_required) return "Arbeitgeber";
  return "Im Paket";
}

function formatDocumentAction(documentEntry, missingEntry) {
  if (missingEntry?.responsibility?.detail) return missingEntry.responsibility.detail;
  if (documentEntry?.review_bucket === "ready_to_sign") return "Jetzt unterschreiben und bereitlegen.";
  if (documentEntry?.review_bucket === "ready_to_upload") return "Zur Einreichung bereithalten.";
  if (documentEntry?.review_bucket === "missing_docs") return "Fehlende Unterlage oder Angabe anfordern.";
  if (documentEntry?.review_bucket === "manual_only") return "Manuell beschaffen oder ausfuellen.";
  if (documentEntry?.fillability_class === "structured_transfer_only") return "Transferhilfe fuer das Original nutzen.";
  return documentEntry?.review_note || documentEntry?.fillability_reason || "Unterlage im Paketstand pruefen.";
}

export default function StbAuthorityFilingEmployerWorkspace(props) {
  const {
    row,
    filingData,
    detailState,
    requestedRequestId,
    activeTab,
    onTabChange,
    onExportKit,
    onFinalizeSubmissionPack,
    onDownloadPreparedAsset,
    onDownloadAuthorityDocument,
    onUpdateAuthorityTask,
    onCreateCandidateRequest,
    onUpdateCandidateRequest,
    onTaskNoteChange,
    getDeadlineTone,
    getFreshnessMeta,
    getResponsibilityMeta,
    getReviewBucketMeta,
    formatCoverageLevel,
    formatDateTime,
    formatDelta,
    formatHistoryAction,
    formatRequestStatus,
    formatResponseStatus,
    formatTaskStatus,
    formatUrgencyLabel,
  } = props;

  const documents = filingData.documents || [];
  const missingDocuments = filingData.missing_documents || [];
  const missingById = new Map(missingDocuments.map((entry) => [String(entry.document_id), entry]));
  const openTasks = filingData.task_board?.open_tasks || [];
  const openEmployerTask = openTasks.find((task) => task.owner_role === "employer") || openTasks[0] || null;
  const allRequests = filingData.candidate_request_board?.all_requests || [];
  const activeRequests = allRequests.filter((entry) => !["fulfilled", "obsolete", "expired"].includes(entry.status));
  const openCandidateRequest = activeRequests.find((entry) => !entry.awaiting_employer_review) || activeRequests[0] || null;
  const primaryBlocker = filingData.completion_workflow?.next_blockers?.[0]
    || activeRequests.find((entry) => entry.overdue)
    || openTasks.find((task) => task.status === "blocked")
    || null;
  const preparedDocumentCount = documents.filter((entry) => entry.review_bucket !== "missing_docs").length;
  const openPointCount = (filingData.task_board?.open_count || 0) + (filingData.candidate_request_board?.active_requests?.length || 0);
  const requestGroups = {
    open: allRequests.filter((entry) => !["fulfilled", "obsolete", "expired"].includes(entry.status) && !entry.awaiting_employer_review),
    review: allRequests.filter((entry) => entry.awaiting_employer_review || ["response_received", "response_under_review", "response_incomplete", "response_rejected"].includes(entry.response_status)),
    done: allRequests.filter((entry) => ["fulfilled", "obsolete", "expired"].includes(entry.status)),
  };
  const latestPack = filingData.final_submission_packs?.[0] || null;
  const latestHistory = filingData.history?.[0] || null;

  return (
    <div className="mt-4 space-y-4">
      <div className="rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 via-white to-sky-50 p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Fallstatus / Ueberblick</p>
            <p className="mt-2 text-2xl font-semibold text-slate-900">
              {filingData.employer_ready_pack?.cover_title || filingData.filing_packet_summary?.headline}
            </p>
            <p className="mt-2 text-sm text-slate-600">
              {filingData.classification?.procedure_explainer?.label || filingData.classification?.pathway_title || "Verfahrenspfad"}
              {" · "}
              {filingData.classification?.federal_state || "Bundesland offen"}
              {filingData.classification?.city ? ` / ${filingData.classification.city}` : ""}
            </p>
            <p className="mt-2 text-sm text-slate-600">
              Reifegrad: <span className="font-semibold text-slate-900">{filingData.review_workflow?.overall_label || "wird berechnet"}</span>
              {" · "}
              Einreichungsstatus: <span className="font-semibold text-slate-900">{filingData.submission_pack_state?.current_stage_label || "Working Pack"}</span>
            </p>
            <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-xl border border-white/70 bg-white/90 p-3">
                <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Groesster Blocker</p>
                <p className="mt-2 font-semibold text-slate-900">{primaryBlocker?.title || "Aktuell kein dominanter Blocker"}</p>
                <p className="mt-1 text-sm text-slate-600">
                  {primaryBlocker?.action_label || primaryBlocker?.request_reason || primaryBlocker?.description || filingData.submission_pack_state?.stage_reason}
                </p>
              </div>
              <div className="rounded-xl border border-white/70 bg-white/90 p-3">
                <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Naechste Arbeitgeberaktion</p>
                <p className="mt-2 font-semibold text-slate-900">{openEmployerTask?.title || "Aktuell keine direkte Arbeitgeberaktion"}</p>
                <p className="mt-1 text-sm text-slate-600">{openEmployerTask?.description || openEmployerTask?.action_label || "Der Fall ist aus Arbeitgebersicht aktuell auf Stand."}</p>
              </div>
              <div className="rounded-xl border border-white/70 bg-white/90 p-3">
                <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Naechste Bewerberaktion</p>
                <p className="mt-2 font-semibold text-slate-900">{openCandidateRequest?.title || "Aktuell nichts direkt vom Bewerber offen"}</p>
                <p className="mt-1 text-sm text-slate-600">{formatRequestNextStep(openCandidateRequest)}</p>
              </div>
              <div className="rounded-xl border border-white/70 bg-white/90 p-3">
                <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Vorbereitung</p>
                <p className="mt-2 font-semibold text-slate-900">{preparedDocumentCount} Unterlagen vorbereitet</p>
                <p className="mt-1 text-sm text-slate-600">{openPointCount} offene Punkte bis zum naechsten belastbaren Stand</p>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 lg:max-w-sm lg:justify-end">
            <Button variant="outline" className="dashboard-inline-button" onClick={() => onExportKit(row.id, "json")} disabled={detailState.exporting}>
              JSON Export
            </Button>
            <Button variant="outline" className="dashboard-inline-button" onClick={() => onExportKit(row.id, "html")} disabled={detailState.exporting}>
              HTML Export
            </Button>
            <span className="rounded-full border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
              Lokal gepruefte Dokumente {filingData.document_library_status?.current_locally_checked_documents || 0}
            </span>
            <span className="rounded-full border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
              Offene Schritte {filingData.task_board?.open_count || 0}
            </span>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Was jetzt zu tun ist</p>
            <p className="mt-2 text-lg font-semibold text-slate-900">Die Seite fuehrt jetzt ueber wenige klare Schritte statt ueber alle Daten gleichzeitig.</p>
            <p className="mt-1 text-sm text-slate-600">
              Signierbar {filingData.task_board?.signable_count || 0} · uploadbar {filingData.task_board?.uploadable_count || 0} · blocker {filingData.completion_workflow?.blocking_count || 0}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="dashboard-inline-button" onClick={() => onTabChange("candidate")}>
              Bewerberanforderungen
            </Button>
            <Button variant="outline" className="dashboard-inline-button" onClick={() => onTabChange("documents")}>
              Dokumente
            </Button>
            <Button variant="outline" className="dashboard-inline-button" onClick={() => onTabChange("pack")}>
              Einreichungspaket
            </Button>
          </div>
        </div>
        <div className="mt-4 grid gap-3 xl:grid-cols-3">
          <div className="rounded-xl border border-sky-200 bg-sky-50 p-4">
            <p className="text-xs uppercase tracking-[0.14em] text-sky-700">Arbeitgeber</p>
            <p className="mt-2 font-semibold text-slate-900">{openEmployerTask?.title || "Kein direkter Arbeitgeber-Task offen"}</p>
            <p className="mt-1 text-sm text-slate-700">{openEmployerTask?.action_label || openEmployerTask?.description || "Der Fall braucht aktuell keine unmittelbare Arbeitgeberaktion."}</p>
            {openEmployerTask ? (
              <div className="mt-3 flex flex-wrap gap-2">
                <Button variant="outline" className="dashboard-inline-button" onClick={() => onUpdateAuthorityTask(row.id, openEmployerTask, "progress")}>In Arbeit</Button>
                <Button variant="outline" className="dashboard-inline-button" onClick={() => onUpdateAuthorityTask(row.id, openEmployerTask, "complete")}>Als erledigt markieren</Button>
              </div>
            ) : null}
          </div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
            <p className="text-xs uppercase tracking-[0.14em] text-emerald-700">Bewerber</p>
            <p className="mt-2 font-semibold text-slate-900">{openCandidateRequest?.title || "Keine offene Bewerberanforderung"}</p>
            <p className="mt-1 text-sm text-slate-700">{formatRequestNextStep(openCandidateRequest)}</p>
            {openCandidateRequest ? (
              <div className="mt-3 flex flex-wrap gap-2">
                <span className={`rounded-full border px-2 py-1 text-[11px] ${getDeadlineTone(openCandidateRequest)}`}>
                  {formatRequestReadableState(openCandidateRequest, formatRequestStatus)}
                </span>
                <span className="rounded-full border border-white bg-white px-2 py-1 text-[11px] text-slate-600">
                  Faellig {formatDateTime(openCandidateRequest.due_at)}
                </span>
              </div>
            ) : null}
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
            <p className="text-xs uppercase tracking-[0.14em] text-amber-700">Blocker</p>
            <p className="mt-2 font-semibold text-slate-900">{primaryBlocker?.title || "Kein aktiver Blocker"}</p>
            <p className="mt-1 text-sm text-slate-700">
              {primaryBlocker?.action_label || primaryBlocker?.request_reason || primaryBlocker?.description || "Der Fall ist aktuell nicht durch einen grossen Einzelpunkt blockiert."}
            </p>
          </div>
        </div>
        {(filingData.completion_workflow?.summary_lines?.length || filingData.task_board?.summary_lines?.length) ? (
          <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">
              {[...(filingData.completion_workflow?.summary_lines || []), ...(filingData.task_board?.summary_lines || [])].slice(0, 5).map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        ) : null}
        <FilingDisclosure
          title="Alle offenen Schritte im Detail"
          subtitle="Hier bleiben die persistente Task-Logik und alle Aktionen erhalten, aber nicht mehr als Primaerflaeche."
        >
          <div className="space-y-3">
            {openTasks.length ? openTasks.slice(0, 10).map((task) => (
              <div key={`task-${task.task_id}`} className="rounded-lg border border-slate-200 bg-white p-3">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold text-slate-900">{task.title}</p>
                      <span className={`rounded-full border px-2 py-1 text-[11px] ${getResponsibilityMeta(task.owner_role).tone}`}>{formatOwnerRoleLabel(task.owner_role)}</span>
                      <span className={`rounded-full border px-2 py-1 text-[11px] ${getReviewBucketMeta(task.source_bucket || "manual_only").tone}`}>{formatReviewBucketLabel(task.source_bucket || task.source_type)}</span>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">{task.description || task.action_label || "Offener Schritt im Filing-Fall."}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      Status {formatTaskStatus(task.status)} · Prioritaet {task.priority || "mittel"}
                      {task.requires_signature ? " · Unterschrift noetig" : ""}
                      {task.requires_upload ? " · Upload noetig" : ""}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" className="dashboard-inline-button" onClick={() => onUpdateAuthorityTask(row.id, task, "complete")}>Erledigt</Button>
                    <Button variant="outline" className="dashboard-inline-button" onClick={() => onUpdateAuthorityTask(row.id, task, task.status === "blocked" ? "reopen" : "block")}>
                      {task.status === "blocked" ? "Entblocken" : "Blockieren"}
                    </Button>
                    <Button variant="outline" className="dashboard-inline-button" onClick={() => onUpdateAuthorityTask(row.id, task, "progress")}>In Arbeit</Button>
                    {task.requires_candidate_input || task.owner_role === "candidate" ? (
                      <Button variant="outline" className="dashboard-inline-button" onClick={() => onCreateCandidateRequest(row.id, task, true)}>An Bewerber senden</Button>
                    ) : null}
                  </div>
                </div>
                <div className="mt-3 flex flex-col gap-2 lg:flex-row">
                  <textarea
                    className="min-h-[74px] flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none"
                    placeholder="Kurze Arbeitsnotiz zu diesem Schritt"
                    value={detailState.taskNotes?.[String(task.task_id)] ?? task.completion_note ?? ""}
                    onChange={(event) => onTaskNoteChange(row.id, task.task_id, event.target.value)}
                  />
                  <Button variant="outline" className="dashboard-inline-button self-start" onClick={() => onUpdateAuthorityTask(row.id, task, "note")}>
                    Notiz speichern
                  </Button>
                </div>
              </div>
            )) : (
              <p className="text-sm text-slate-600">Keine offenen Filing-Schritte mehr.</p>
            )}
          </div>
        </FilingDisclosure>
      </div>

      <Tabs value={activeTab} onValueChange={onTabChange} className="space-y-4">
        <TabsList className="h-auto w-full flex-wrap justify-start gap-2 rounded-2xl bg-slate-100 p-2">
          <TabsTrigger value="candidate" className="rounded-xl">Kandidatenanforderungen</TabsTrigger>
          <TabsTrigger value="documents" className="rounded-xl">Dokumente & Formulare</TabsTrigger>
          <TabsTrigger value="pack" className="rounded-xl">Finales Einreichungspaket</TabsTrigger>
          <TabsTrigger value="history" className="rounded-xl">Verlauf & Historie</TabsTrigger>
        </TabsList>

        <TabsContent value="candidate" className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Kandidatenanforderungen</p>
                <p className="mt-2 text-lg font-semibold text-slate-900">Offene Anforderungen an den Bewerber sind jetzt separat und leicht lesbar gebuendelt.</p>
                <p className="mt-1 text-sm text-slate-600">Was fehlt, warum es gebraucht wird, bis wann es benoetigt wird und was als Naechstes passiert.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-600">{requestGroups.open.length} offen</span>
                <span className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs text-sky-700">{requestGroups.review.length} in Pruefung</span>
                <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs text-emerald-700">{requestGroups.done.length} erledigt</span>
              </div>
            </div>
            {filingData.delivery_board ? (
              <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                Letzte Zustellung: Mails gesendet {filingData.delivery_board.email_sent_count || 0} · offen/pending {filingData.delivery_board.email_pending_count || 0} · Fehler {filingData.delivery_board.delivery_failure_count || 0} · Retry {filingData.delivery_board.retry_count || 0}
              </div>
            ) : null}
            <div className="mt-4 grid gap-4 xl:grid-cols-3">
              {[
                { key: "open", title: "Offene Anforderungen", description: "Hier fehlt noch etwas vom Bewerber." },
                { key: "review", title: "In Pruefung", description: "Rueckmeldungen sind eingegangen und muessen bewertet werden." },
                { key: "done", title: "Erledigt", description: "Diese Anforderungen sind abgeschlossen oder nicht mehr relevant." },
              ].map((group) => (
                <div key={group.key} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-sm font-semibold text-slate-900">{group.title}</p>
                  <p className="mt-1 text-xs text-slate-500">{group.description}</p>
                  <div className="mt-3 space-y-3">
                    {(requestGroups[group.key] || []).length ? (requestGroups[group.key] || []).slice(0, 6).map((requestEntry) => (
                      <div
                        key={`request-${requestEntry.request_id}`}
                        className={`rounded-lg border bg-white p-3 ${String(requestEntry.request_id) === String(requestedRequestId) ? "border-teal-300 ring-2 ring-teal-100" : "border-slate-200"}`}
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-semibold text-slate-900">{requestEntry.title}</p>
                          <span className={`rounded-full border px-2 py-1 text-[11px] ${getDeadlineTone(requestEntry)}`}>{formatRequestReadableState(requestEntry, formatRequestStatus)}</span>
                        </div>
                        <p className="mt-2 text-sm text-slate-600">{requestEntry.request_reason || requestEntry.description || "Filing-Anforderung"}</p>
                        <p className="mt-2 text-xs text-slate-500">
                          Frist {formatDateTime(requestEntry.due_at)} · Letzte Aktivitaet {formatDateTime(requestEntry.last_activity_at)} · Naechster Schritt {formatRequestNextStep(requestEntry)}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">
                          Dringlichkeit {formatUrgencyLabel(requestEntry.urgency)} · Reminder {requestEntry.reminder_count || 0}x · Letzte Mail {formatDateTime(requestEntry.delivery_summary?.last_email_delivery_at)}
                        </p>
                        {requestEntry.response_status ? (
                          <p className="mt-1 text-xs text-slate-500">Review-Stand: {formatResponseStatus(requestEntry.response_status)}</p>
                        ) : null}
                        {requestEntry.latest_response_summary?.preview_text ? (
                          <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                            <strong className="text-slate-900">Letzte Rueckmeldung:</strong> {requestEntry.latest_response_summary.preview_text}
                          </div>
                        ) : null}
                        {group.key !== "done" ? (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {requestEntry.status === "draft" ? (
                              <Button variant="outline" className="dashboard-inline-button" onClick={() => onUpdateCandidateRequest(row.id, requestEntry, "sent")}>Senden</Button>
                            ) : null}
                            {["sent", "viewed"].includes(requestEntry.status) ? (
                              <Button variant="outline" className="dashboard-inline-button" onClick={() => onUpdateCandidateRequest(row.id, requestEntry, "answered", { response_status: "response_received" })}>Antwort erhalten</Button>
                            ) : null}
                            {requestEntry.status === "answered" ? (
                              <Button variant="outline" className="dashboard-inline-button" onClick={() => onUpdateCandidateRequest(row.id, requestEntry, "answered", { response_status: "response_under_review" })}>In Pruefung</Button>
                            ) : null}
                            {["answered", "viewed", "sent"].includes(requestEntry.status) ? (
                              <Button variant="outline" className="dashboard-inline-button" onClick={() => onUpdateCandidateRequest(row.id, requestEntry, "fulfilled", { response_status: "response_accepted" })}>Akzeptieren</Button>
                            ) : null}
                            {requestEntry.status === "answered" ? (
                              <Button variant="outline" className="dashboard-inline-button" onClick={() => onUpdateCandidateRequest(row.id, requestEntry, "answered", { response_status: "response_incomplete" })}>Unvollstaendig</Button>
                            ) : null}
                            {requestEntry.status === "answered" ? (
                              <Button variant="outline" className="dashboard-inline-button" onClick={() => onUpdateCandidateRequest(row.id, requestEntry, "answered", { response_status: "response_rejected" })}>Nacharbeit</Button>
                            ) : null}
                            {!["fulfilled", "obsolete", "expired"].includes(requestEntry.status) ? (
                              <Button variant="outline" className="dashboard-inline-button" onClick={() => onUpdateCandidateRequest(row.id, requestEntry, "obsolete")}>Nicht mehr relevant</Button>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    )) : (
                      <p className="text-sm text-slate-500">In diesem Bereich liegt aktuell nichts.</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="documents" className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Dokumente & Formulare</p>
                <p className="mt-2 text-lg font-semibold text-slate-900">Die Dokumente bleiben vollstaendig erhalten, aber zeigen standardmaessig nur noch das, was fuer die Arbeit jetzt relevant ist.</p>
                <p className="mt-1 text-sm text-slate-600">Titel, kurzer Sinn, Status, Verantwortlichkeit und naechste Aktion vorne. Technische Tiefe erst auf Wunsch.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs text-emerald-700">{filingData.fillability_summary?.fillable_pdf || 0} direkt vorbereitbar</span>
                <span className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs text-sky-700">{filingData.fillability_summary?.structured_transfer_only || 0} Transferhilfen</span>
                <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-600">{missingDocuments.length} fehlen/manuell</span>
              </div>
            </div>
            <div className="mt-4 space-y-3">
              {documents.map((entry) => {
                const missingEntry = missingById.get(String(entry.id));
                return (
                  <div key={entry.id} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-semibold text-slate-900">{entry.title}</p>
                          <span className={`rounded-full border px-2 py-1 text-[11px] ${getReviewBucketMeta(entry.review_bucket).tone}`}>{formatReviewBucketLabel(entry.review_bucket)}</span>
                        </div>
                        <p className="mt-2 text-sm text-slate-600">{entry.review_note || entry.fillability_reason || entry.authority || "Offizielle Unterlage im Filing-Paket."}</p>
                        <p className="mt-2 text-xs text-slate-500">
                          Verantwortlich: {formatDocumentResponsibility(entry, missingEntry)} · Jetzt zu tun: {formatDocumentAction(entry, missingEntry)}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {entry.download_url ? (
                          <Button variant="outline" className="dashboard-inline-button" onClick={() => onDownloadAuthorityDocument(row.id, entry)} disabled={detailState.exporting}>
                            Offizielles Dokument
                          </Button>
                        ) : null}
                        <span className="rounded-full border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-600">{formatFillabilityClass(entry.fillability_class)}</span>
                      </div>
                    </div>
                    <FilingDisclosure
                      title="Dokumentdetails"
                      subtitle="Fillability, Felder, Warnhinweise, Bibliotheksstand und technische Ableitung nur bei Bedarf."
                    >
                      <div className="grid gap-4 xl:grid-cols-[1.1fr,0.9fr]">
                        <div className="space-y-3">
                          <div className="rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-700">
                            <p>Status: {entry.checklist_status?.status || "unbekannt"}</p>
                            <p>Fillability: {formatFillabilityClass(entry.fillability_class)} · Abdeckung {formatCoverageLevel(entry.field_coverage_level)} · Confidence {entry.fillability_confidence ?? "n/a"}</p>
                            <p>PDF-Felder {entry.pdf_field_count ?? 0} · Auto {entry.autofill_field_count ?? 0} · Manuell {entry.manual_field_count ?? 0}</p>
                            <p>Letzter Download {entry.downloaded_at || "noch nicht lokal"} · Letzte Pruefung {entry.last_checked_at || "n/a"}</p>
                          </div>
                          {entry.plausibility_warnings?.length ? (
                            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                              <p className="text-sm font-semibold text-amber-800">Plausibilitaetshinweise</p>
                              <ul className="mt-2 list-disc pl-5 text-sm text-amber-700">
                                {entry.plausibility_warnings.map((note) => <li key={note}>{note}</li>)}
                              </ul>
                            </div>
                          ) : null}
                        </div>
                        <div className="space-y-3">
                          {entry.review_requirements?.length ? (
                            <div className="rounded-lg border border-slate-200 bg-white p-3">
                              <p className="text-sm font-semibold text-slate-900">Review-Anforderungen</p>
                              <div className="mt-2 flex flex-wrap gap-2">
                                {entry.review_requirements.map((requirement) => (
                                  <span key={`${entry.id}-${requirement}`} className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-600">
                                    {requirement}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ) : null}
                          {entry.freshness ? (
                            <div className="rounded-lg border border-slate-200 bg-white p-3">
                              <p className="text-sm font-semibold text-slate-900">Dokumentenstand</p>
                              <p className="mt-2 text-sm text-slate-700">{entry.freshness.label}</p>
                              <span className={`mt-2 inline-flex rounded-full border px-2 py-1 text-[11px] ${getFreshnessMeta(entry.freshness.freshness_status).tone}`}>{entry.freshness.label}</span>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </FilingDisclosure>
                  </div>
                );
              })}
            </div>
          </div>

          <FilingDisclosure
            title="Behoerden & Zustaendigkeiten"
            subtitle="Foederale Aufloesung, Behoerdenliste und Kontaktblock bleiben erhalten, aber als Sekundaerebene."
          >
            <div className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <p className="text-sm font-semibold text-slate-900">Foederale Aufloesung</p>
                <p className="mt-2 text-sm text-slate-600">{filingData.jurisdiction_resolution?.summary}</p>
                <div className="mt-3 grid gap-3 xl:grid-cols-3">
                  {(filingData.jurisdiction_resolution?.layers || []).map((layer) => (
                    <div key={layer.level} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">{layer.level}</p>
                      <p className="mt-2 font-semibold text-slate-900">{layer.title}</p>
                      <p className="mt-1 text-sm text-slate-600">{layer.effect}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid gap-4 xl:grid-cols-2">
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <p className="text-sm font-semibold text-slate-900">Behoerdenliste</p>
                  <div className="mt-3 space-y-3">
                    {(filingData.authority_matrix || []).map((entry) => (
                      <div key={`${entry.title}-${entry.level}`} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <p className="font-semibold text-slate-900">{entry.title}</p>
                        <p className="text-sm text-slate-600">{entry.scope}</p>
                        <p className="mt-1 text-xs text-slate-500">{entry.resolved_state || "Bund"} · {entry.resolved_city || "ohne Ortsbindung"}</p>
                        <a href={entry.source_url} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs text-sky-700">Quelle oeffnen</a>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <p className="text-sm font-semibold text-slate-900">Kontaktblock</p>
                  <div className="mt-3 space-y-3">
                    {(filingData.employer_ready_pack?.authority_contact_block || []).map((entry) => (
                      <div key={`${entry.title}-${entry.city || entry.state}`} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <p className="font-semibold text-slate-900">{entry.title}</p>
                        <p className="mt-1 text-sm text-slate-600">{entry.state}{entry.city ? ` / ${entry.city}` : ""}</p>
                        <a href={entry.source_url} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs text-sky-700">Offizielle Quelle</a>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </FilingDisclosure>

          <FilingDisclosure
            title="Dokumentenstand & Aktualitaet"
            subtitle="Bibliotheksfrische, letzte Aktualisierung und Unsicherheiten bleiben erhalten, aber nur als Detailbereich."
          >
            <div className="space-y-4">
              {filingData.document_refresh ? (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
                  Letzte Bibliotheksaktualisierung: {filingData.document_refresh.updated_count} aktualisiert, {filingData.document_refresh.skipped_count} uebersprungen, {filingData.document_refresh.error_count} Fehler.
                </div>
              ) : null}
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Lokal geprueft</p>
                  <p className="mt-2 text-xl font-semibold text-slate-900">{filingData.document_library_status?.current_locally_checked_documents || 0}</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Aktualitaet unklar</p>
                  <p className="mt-2 text-xl font-semibold text-slate-900">{filingData.document_library_status?.local_unknown_currency_documents || 0}</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Nur beschrieben</p>
                  <p className="mt-2 text-xl font-semibold text-slate-900">{filingData.document_library_status?.officially_described_documents || 0}</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Refresh-faehig</p>
                  <p className="mt-2 text-xl font-semibold text-slate-900">{filingData.document_library_status?.refreshable_documents || 0}</p>
                </div>
              </div>
            </div>
          </FilingDisclosure>
        </TabsContent>

        <TabsContent value="pack" className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Finales Einreichungspaket</p>
                <p className="mt-2 text-lg font-semibold text-slate-900">{filingData.submission_pack_state?.current_stage_label || "Working Pack"}</p>
                <p className="mt-1 text-sm text-slate-600">{filingData.submission_pack_state?.stage_reason}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button className="dashboard-action-button" onClick={() => onFinalizeSubmissionPack(row.id)} disabled={!filingData.submission_pack_state?.can_finalize || detailState.finalizingPack}>
                  {detailState.finalizingPack ? "Pack wird finalisiert..." : "Finales Paket erzeugen"}
                </Button>
                <Button variant="outline" className="dashboard-inline-button" onClick={() => onExportKit(row.id, "html")} disabled={detailState.exporting}>
                  Arbeitsansicht exportieren
                </Button>
              </div>
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Einreichungsreif?</p>
                <p className="mt-2 font-semibold text-slate-900">{filingData.submission_pack_state?.can_finalize ? "Ja, finalisierbar" : "Noch nicht"}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Offene Schritte</p>
                <p className="mt-2 font-semibold text-slate-900">{filingData.submission_pack_state?.open_task_count || 0}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Offene Requests</p>
                <p className="mt-2 font-semibold text-slate-900">{filingData.submission_pack_state?.active_request_count || 0}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Finale Pakete</p>
                <p className="mt-2 font-semibold text-slate-900">{filingData.final_submission_packs?.length || 0}</p>
              </div>
            </div>
            {!filingData.submission_pack_state?.can_finalize ? (
              <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                Noch offen bis zum belastbaren Einreichungsstand: {primaryBlocker?.title || filingData.submission_pack_state?.stage_reason}
              </div>
            ) : null}
          </div>

          <div className="grid gap-4 xl:grid-cols-[1.1fr,0.9fr]">
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Aktuell relevantes Paket</p>
              {latestPack ? (
                <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-semibold text-slate-900">Pack {latestPack.pack_no} · {latestPack.stage_label || latestPack.stage}</p>
                    <span className="text-xs text-slate-500">{latestPack.created_at}</span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {(latestPack.pack_assets || []).map((asset) => (
                      <Button key={`pack-asset-${asset.asset_id}`} variant="outline" className="dashboard-inline-button" onClick={() => onDownloadPreparedAsset(row.id, asset)} disabled={detailState.exporting}>
                        {asset.file_name}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="mt-3 text-sm text-slate-600">Noch kein finalisiertes Paket vorhanden.</p>
              )}
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Pack-Uebersicht</p>
              <div className="mt-3 space-y-3">
                {(filingData.filing_packet_summary?.summary || []).slice(0, 6).map((line) => (
                  <div key={line} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">{line}</div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="history" className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Verlauf / Historie</p>
                <p className="mt-2 text-lg font-semibold text-slate-900">Aenderungen, Versionen und Vergleich bleiben verfuegbar, aber als Analyseebene statt als Hauptbuehne.</p>
                <p className="mt-1 text-sm text-slate-600">Standardansicht reduziert, Details nur noch bei Bedarf.</p>
              </div>
              <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                Letzte Version {latestHistory?.version_no || 0}
              </span>
            </div>
          </div>

          <FilingDisclosure
            title="Aenderungen seit letzter Version"
            subtitle="Version Compare bleibt erhalten, aber kompakt und einklappbar."
            defaultOpen
          >
            {filingData.version_compare?.summary_lines?.length ? (
              <div className="space-y-4">
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <ul className="list-disc pl-5 text-sm text-slate-700">
                    {filingData.version_compare.summary_lines.map((line) => <li key={line}>{line}</li>)}
                  </ul>
                </div>
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                  <div className="rounded-lg border border-slate-200 bg-white p-3">
                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Dokumente</p>
                    <p className="mt-2 text-sm text-slate-900">Neu {formatDelta(filingData.version_compare.document_changes?.added?.length || 0)} · Entfallen {formatDelta(-1 * (filingData.version_compare.document_changes?.removed?.length || 0))}</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white p-3">
                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Offene Punkte</p>
                    <p className="mt-2 text-sm text-slate-900">Tasks {formatDelta(filingData.version_compare.open_tasks_delta || 0)} · Requests {formatDelta(filingData.version_compare.active_requests_delta || 0)}</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white p-3">
                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Reifegrad</p>
                    <p className="mt-2 text-sm text-slate-900">{filingData.version_compare.previous_pack_label || "n/a"} -> {filingData.version_compare.current_pack_label || "aktuell"}</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white p-3">
                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Zustellung</p>
                    <p className="mt-2 text-sm text-slate-900">Mails {formatDelta(filingData.version_compare.email_sent_delta || 0)} · Fehler {formatDelta(filingData.version_compare.delivery_failure_delta || 0)}</p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-600">Sobald eine Vorversion existiert, erscheint hier der reduzierte Vergleich zum aktuellen Stand.</p>
            )}
          </FilingDisclosure>

          <FilingDisclosure
            title="Versionen und Verlauf"
            subtitle="Persistente Historie, Final Packs und aeltere Staende in komprimierter Form."
          >
            <div className="space-y-3">
              {filingData.history?.length ? filingData.history.map((entry) => (
                <div key={`${entry.case_id}-${entry.version_no}`} className="rounded-lg border border-slate-200 bg-white p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-semibold text-slate-900">Version {entry.version_no} · {formatHistoryAction(entry.action_type)}</p>
                    <span className="text-xs text-slate-500">{entry.created_at}</span>
                  </div>
                  <p className="mt-1 text-sm text-slate-600">
                    {entry.classification?.procedure_explainer?.label || entry.classification?.procedure || "Pfad"} · {entry.classification?.federal_state || "ohne Bundesland"} · {entry.classification?.city || "ohne Stadt"}
                  </p>
                  <p className="mt-2 text-xs text-slate-500">
                    Offene Tasks {entry.history_summary?.open_tasks_count || 0} · aktive Requests {entry.history_summary?.active_requests_count || 0} · Final Packs {entry.history_summary?.final_pack_count || 0}
                  </p>
                  {entry.previous_version_delta?.summary_lines?.length ? (
                    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <ul className="list-disc pl-5 text-sm text-slate-700">
                        {entry.previous_version_delta.summary_lines.slice(0, 4).map((line) => <li key={`${entry.version_no}-${line}`}>{line}</li>)}
                      </ul>
                    </div>
                  ) : null}
                </div>
              )) : (
                <p className="text-sm text-slate-600">Noch keine gespeicherte Historie vorhanden.</p>
              )}
            </div>
          </FilingDisclosure>
        </TabsContent>
      </Tabs>
    </div>
  );
}
